import { makeAutoObservable } from 'mobx'
import { type IIconsVector, isIconKey } from 'shared/ui/Icon'
import {
  IntegrationKey,
  type IIntegrationKey,
  type IResponseIntegration,
  type IResponseUserIntegration,
} from 'entities/Integrations/api/types'
import { NANGO_GOOGLE_INTEGRATION_KEYS } from 'entities/Integrations/lib/constants'
import { UserIntegration } from 'entities/Integrations/model/UserIntegration'

export class Integration {
  id: number
  key: IIntegrationKey
  name: string
  is_active: boolean
  connected: UserIntegration | null
  disconnected: UserIntegration | null
  oauth: boolean
  canModifySettings: boolean
  is_ampersand: boolean
  is_unified_connection: boolean
  uiConnectedId = ''
  private _loading = false

  constructor(item: IResponseIntegration) {
    this.id = item.id
    this.key = item.key
    this.name = item.name
    this.is_active = item.is_active
    this.oauth = item.oauth
    this.canModifySettings = item.can_modify
    this.connected = item.connected ? new UserIntegration(item.connected) : null
    this.disconnected = item.disconnected ? new UserIntegration(item.disconnected) : null
    this.is_ampersand = item.is_ampersand
    this.is_unified_connection = item.is_unified_connection

    makeAutoObservable(this)
  }

  get icon(): IIconsVector {
    if (this.key === 'close') {
      return 'closeIntegration'
    }
    if (this.key === 'agentforce') {
      return 'salesforce'
    }
    if (this.key === IntegrationKey.google_sheet) {
      return 'googleSheets'
    }
    if (this.key === IntegrationKey.google_doc) {
      return 'googleDocs'
    }
    return isIconKey(this.key) ? this.key : 'salesmsg'
  }

  get isConnected() {
    return Boolean(this.connected?.is_active && this.connected.connection_error === 0)
  }

  get shouldReconnect() {
    return Boolean(this.connected?.is_active && this.connected.connection_error !== 0)
  }

  get url() {
    return this.connected?.url
  }

  get user_credential() {
    return this.connected?.user_credential
  }

  get isConnectedSettingsAvailable() {
    return Boolean(this.isConnected && this.canModifySettings)
  }

  get isBeta() {
    return this.key === IntegrationKey.salesmsg_mcp
  }

  get isSettingsAvailable() {
    const availableZoho = this.key === IntegrationKey.zoho && this.isConnected
    const availableAmpersand = this.is_ampersand && this.isConnected

    return (
      availableAmpersand ||
      this.isConnectedSettingsAvailable ||
      this.key === IntegrationKey.salesforce ||
      this.key === IntegrationKey.salesmsg_mcp ||
      this.key === IntegrationKey.agentforce ||
      availableZoho
    )
  }

  get loading() {
    return this._loading
  }

  setLoading(isLoading: boolean) {
    this._loading = isLoading
  }

  syncOrigin = (origin: IResponseIntegration) => {
    this.id = origin.id
    this.key = origin.key
    this.name = origin.name
    this.is_active = origin.is_active
    this.oauth = origin.oauth
    this.canModifySettings = origin.can_modify
    this.connected = origin.connected ? new UserIntegration(origin.connected) : null
    this.disconnected = origin.disconnected ? new UserIntegration(origin.disconnected) : null
  }

  syncUserIntegration = (userIntegration: IResponseUserIntegration) => {
    if (userIntegration.is_active) {
      this.disconnected = null
      this.connected = new UserIntegration(userIntegration)
    } else {
      this.connected = null
      this.disconnected = new UserIntegration(userIntegration)
    }
  }

  setUiConnectedId = (id: string) => {
    this.uiConnectedId = id
  }

  get isNangoProvider() {
    return this.is_unified_connection
  }

  get isNangoGoogleIntegration() {
    return NANGO_GOOGLE_INTEGRATION_KEYS.includes(this.key)
  }

  get isContactIntegration() {
    return (this.isNangoProvider || this.is_ampersand) && !this.isNangoGoogleIntegration
  }
}
